import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import {
  parseMeasurementHeightInput,
  parseMeasurementValueInput,
  rescaleMeasurementGeometry,
} from "@/lib/geometry";
import { MEASUREMENT_TYPES } from "@/viewport-tools/measurement";

import { computeValue, measurementRequiresHeight } from "./measurement-overlay-helpers";
import type {
  MeasurementEditDraft,
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "./measurement-overlay-types";

interface SaveMeasurementEditsArgs {
  measurementId: Id<"takeoffPilotMeasurements">;
  edits: MeasurementEditDraft;
  measurements: MeasurementOverlayRecord[];
  scale: MeasurementScale | null;
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
}

export async function saveMeasurementEdits({
  measurementId,
  edits,
  measurements,
  scale,
  onUpdateMeasurement,
}: SaveMeasurementEditsArgs) {
  const measurement = measurements.find((item) => item._id === measurementId);
  if (!measurement) {
    throw new Error("Measurement not found");
  }
  if (!scale) {
    throw new Error("Set a scale before editing measurements");
  }

  const requiresHeight = measurementRequiresHeight(
    measurement.tool,
    measurement.measurementType
  );
  let nextHeight: number | undefined = measurement.height;
  if (requiresHeight) {
    if (edits.heightChanged) {
      const parsedHeight = parseMeasurementHeightInput(edits.heightInput);
      if (parsedHeight === null || parsedHeight <= 0) {
        throw new Error("Height must be a positive architectural measurement");
      }
      nextHeight = parsedHeight;
    }
    if (nextHeight === undefined || nextHeight <= 0) {
      throw new Error("Height must be a positive architectural measurement");
    }
  }

  const parsedValue = parseMeasurementValueInput(
    edits.valueInput,
    measurement.measurementType
  );
  if (parsedValue === null || parsedValue <= 0) {
    const unitMessage =
      measurement.measurementType === MEASUREMENT_TYPES.LF
        ? "a positive architectural measurement"
        : "a positive number";
    throw new Error(`Value must be ${unitMessage}`);
  }

  let nextGeometry = measurement.geometry;
  let nextValue = measurement.value;

  if (edits.valueChanged) {
    const baselineValue = computeValue(
      measurement.tool,
      measurement.measurementType,
      measurement.geometry,
      nextHeight,
      scale.pixelsPerFoot,
      scale.pageAspectRatio
    );
    if (!Number.isFinite(baselineValue) || baselineValue <= 0) {
      throw new Error(
        "Measurement can't be rescaled because its current geometry produces no measurable value"
      );
    }
    nextGeometry = rescaleMeasurementGeometry({
      tool: measurement.tool,
      measurementType: measurement.measurementType,
      geometry: measurement.geometry,
      currentValue: baselineValue,
      targetValue: parsedValue,
    });
    nextValue = parsedValue;
  } else if (edits.heightChanged) {
    nextValue = computeValue(
      measurement.tool,
      measurement.measurementType,
      measurement.geometry,
      nextHeight,
      scale.pixelsPerFoot,
      scale.pageAspectRatio
    );
  } else {
    return;
  }

  await onUpdateMeasurement(measurement._id, nextGeometry, nextHeight, nextValue);
}

export type { SaveMeasurementEditsArgs };
